import { uploadVideo } from '../hooks/useVideos';
import logger from '../utils/logger';

/**
 * Background Upload Queue Service
 * Handles media uploads independently of component lifecycle
 */
class UploadQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * Add media to the upload queue
   */
  enqueue(mediaFile, eventId, guestName, guestEmail) {
    const uploadTask = {
      id: Date.now(),
      mediaFile,
      eventId,
      guestName,
      guestEmail,
      status: 'pending',
      retries: 0,
      maxRetries: 3
    };

    this.queue.push(uploadTask);
    logger.log(`[UploadQueue] Added media to queue. Queue size: ${this.queue.length}`);

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }

    return uploadTask.id;
  }

  /**
   * Process the upload queue
   */
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const task = this.queue[0];

      try {
        logger.log(`[UploadQueue] Uploading media ${task.id}...`);
        task.status = 'uploading';

        const result = await uploadVideo(
          task.mediaFile,
          task.eventId,
          task.guestName,
          task.guestEmail
        );

        if (result.success) {
          logger.log(`[UploadQueue] Successfully uploaded media ${task.id}`);
          task.status = 'completed';
          // Remove completed task from queue
          this.queue.shift();
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        logger.error(`[UploadQueue] Upload failed for media ${task.id}:`, error);

        // Retry logic
        task.retries++;
        if (task.retries < task.maxRetries) {
          logger.log(`[UploadQueue] Retrying upload (${task.retries}/${task.maxRetries})...`);
          task.status = 'retrying';
          // Wait 2 seconds before retry
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          logger.error(`[UploadQueue] Max retries reached for video ${task.id}`);
          task.status = 'failed';
          // Remove failed task from queue
          this.queue.shift();
        }
      }
    }

    this.isProcessing = false;
    logger.log('[UploadQueue] Queue processing complete');
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing,
      tasks: this.queue.map(t => ({
        id: t.id,
        status: t.status,
        retries: t.retries
      }))
    };
  }
}

// Create singleton instance
export const uploadQueue = new UploadQueue();
